/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { AppState, Platform } from 'react-native';
import NetInfo, { type NetInfoState } from '@react-native-community/netinfo';
import { onlineManager } from '@tanstack/react-query';

type NetworkSnapshot = `${'0' | '1'}:${'0' | '1'}`;
type NetInfoConnectionStatus = boolean | undefined;
export type NetworkStatus = {
  isConnected: boolean;
  isOffline: boolean;
  isNetworkStatusKnown: boolean;
};

let networkIsConnected = true;
// Native starts optimistic; NetInfo confirms whether the first value is real.
let networkStatusKnown = Platform.OS === 'web';
let netInfoUnsubscribe: (() => void) | undefined;
let appStateSubscription: { remove: () => void } | undefined;
let pendingMonitorStopTimeout: ReturnType<typeof setTimeout> | undefined;
let isAppActive =
  AppState.currentState !== 'background' &&
  AppState.currentState !== 'inactive';
const listeners = new Set<() => void>();
let providerSubscriberCount = 0;
let networkRefreshPromise: Promise<NetworkStatus> | undefined;
const NETWORK_REFRESH_TIMEOUT_MS = 3000;
const WEB_NETWORK_SNAPSHOT = '1:1';
const REACHABILITY_URL = 'https://www.airasia.com/generate_204';
let netInfoConfigured = false;

const ensureNetInfoConfigured = () => {
  if (Platform.OS === 'web' || netInfoConfigured) {
    return;
  }

  NetInfo.configure({
    reachabilityUrl: REACHABILITY_URL,
    reachabilityMethod: 'HEAD',
    reachabilityTest: async (response) => response.status === 204,
    useNativeReachability: false,
  });

  netInfoConfigured = true;
};

const buildNetworkSnapshot = (
  isConnected: boolean,
  isKnown: boolean,
): NetworkSnapshot => `${isConnected ? '1' : '0'}:${isKnown ? '1' : '0'}`;

const getConnectionStatusFromNetInfo = (
  state: NetInfoState,
): NetInfoConnectionStatus => {
  if (state.isConnected !== true || state.isInternetReachable === false) {
    return false;
  }

  if (state.isInternetReachable === null) {
    return undefined;
  }

  return true;
};

const clearPendingMonitorStopTimeout = () => {
  if (!pendingMonitorStopTimeout) {
    return;
  }

  clearTimeout(pendingMonitorStopTimeout);
  pendingMonitorStopTimeout = undefined;
};

const applyNetworkIsConnected = (isConnected: boolean) => {
  if (networkIsConnected === isConnected && networkStatusKnown) {
    return;
  }

  networkIsConnected = isConnected;
  networkStatusKnown = true;
  onlineManager.setOnline(isConnected);
  listeners.forEach((listener) => listener());
};

const setNetworkIsConnected = (isConnected: boolean) => {
  if (isConnected) {
    applyNetworkIsConnected(true);
    return;
  }

  if (!isAppActive) {
    // Foreground refresh reconciles the real state; avoid stale background drops.
    return;
  }

  applyNetworkIsConnected(false);
};

const setNetworkIsConnectedFromNetInfo = (state: NetInfoState) => {
  const connectionStatus = getConnectionStatusFromNetInfo(state);
  if (connectionStatus !== undefined) {
    setNetworkIsConnected(connectionStatus);
  }
};

const refreshNetInfoWithTimeout = async () => {
  let timeout: number | ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      NetInfo.refresh(),
      new Promise<undefined>((resolve) => {
        timeout = setTimeout(resolve, NETWORK_REFRESH_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
};

const getCurrentNetworkStatus = (): NetworkStatus => ({
  isConnected: networkIsConnected,
  isOffline: !networkIsConnected,
  isNetworkStatusKnown: networkStatusKnown,
});

export const refreshNetworkStatus = async () => {
  if (networkRefreshPromise) {
    return networkRefreshPromise;
  }

  // Share the native refresh so pull-to-refresh waits for foreground checks.
  networkRefreshPromise = (async () => {
    try {
      const state = await refreshNetInfoWithTimeout();
      if (!state) {
        return getCurrentNetworkStatus();
      }

      setNetworkIsConnectedFromNetInfo(state);
      return getCurrentNetworkStatus();
    } catch {
      // Keep the last known state when NetInfo cannot refresh.
      return getCurrentNetworkStatus();
    }
  })().finally(() => {
    networkRefreshPromise = undefined;
  });

  return networkRefreshPromise;
};

const ensureNetworkMonitor = () => {
  clearPendingMonitorStopTimeout();

  if (Platform.OS === 'web' || netInfoUnsubscribe) {
    return;
  }

  ensureNetInfoConfigured();
  netInfoUnsubscribe = NetInfo.addEventListener(
    setNetworkIsConnectedFromNetInfo,
  );

  appStateSubscription = AppState.addEventListener('change', (status) => {
    isAppActive = status === 'active';

    if (status === 'active') {
      // RN foreground is the safest point to reconcile stale native reachability.
      void refreshNetworkStatus();
    }
  });

  void refreshNetworkStatus();
};

const stopNetworkMonitor = () => {
  clearPendingMonitorStopTimeout();
  netInfoUnsubscribe?.();
  netInfoUnsubscribe = undefined;
  appStateSubscription?.remove();
  appStateSubscription = undefined;
};

const scheduleNetworkMonitorStop = () => {
  if (pendingMonitorStopTimeout) {
    return;
  }

  pendingMonitorStopTimeout = setTimeout(() => {
    pendingMonitorStopTimeout = undefined;

    if (listeners.size === 0 && providerSubscriberCount === 0) {
      stopNetworkMonitor();
    }
  }, 0);
};

const subscribeToNetworkStatus = (listener: () => void) => {
  if (Platform.OS === 'web') {
    return () => undefined;
  }

  listeners.add(listener);
  ensureNetworkMonitor();
  listener();

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0 && providerSubscriberCount === 0) {
      scheduleNetworkMonitorStop();
    }
  };
};

const getNetworkSnapshot = () => {
  if (Platform.OS === 'web') {
    return WEB_NETWORK_SNAPSHOT;
  }

  return buildNetworkSnapshot(networkIsConnected, networkStatusKnown);
};

export const useInitializeNetworkStatus = () => {
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    ensureNetInfoConfigured();
    providerSubscriberCount += 1;
    ensureNetworkMonitor();

    return () => {
      providerSubscriberCount -= 1;

      if (providerSubscriberCount === 0 && listeners.size === 0) {
        scheduleNetworkMonitorStop();
      }
    };
  }, []);
};

export const useNetworkStatus = () => {
  const snapshot = useSyncExternalStore(
    subscribeToNetworkStatus,
    getNetworkSnapshot,
    () => WEB_NETWORK_SNAPSHOT,
  );
  const [isConnectedValue, isKnownValue] = snapshot.split(':');
  const isConnected = isConnectedValue === '1';

  return {
    isConnected,
    isOffline: !isConnected,
    isNetworkStatusKnown: isKnownValue === '1',
  };
};
